export interface OHLCVData {
  o: number;
  h: number;
  l: number;
  c: number;
  v?: number;
  t: number;
  vw?: number;
}

export interface SRLevels {
  poc: number;
  supports: number[];
  resistances: number[];
}

export function findVolumeProfileSR(
  data: OHLCVData[],
  step = 0.1,
  topN = 3
): SRLevels {
  if (!data.length) return { poc: 0, supports: [], resistances: [] };

  let minPrice = data[0].l;
  let maxPrice = data[0].h;
  data.forEach(bar => {
    minPrice = Math.min(minPrice, bar.l);
    maxPrice = Math.max(maxPrice, bar.h);
  });

  const volumeBins: Record<string, number> = {};
  for (let price = minPrice; price <= maxPrice; price += step) {
    volumeBins[price.toFixed(2)] = 0;
  }

  data.forEach(bar => {
    // Use VWAP if available, otherwise fallback to typical price
    const priceForBin = typeof bar.vw === 'number'
      ? bar.vw
      : (bar.h + bar.l + bar.c) / 3;
    const key = (Math.round(priceForBin / step) * step).toFixed(2);
    volumeBins[key] += bar.v ?? 1;
  });

  const currentPrice = data.length > 0 ? data[data.length - 1].c : 0;
  const sorted = Object.entries(volumeBins)
    .map(([price, volume]) => ({ price: parseFloat(price), volume }))
    .sort((a, b) => b.volume - a.volume);

  const poc = sorted[0]?.price ?? 0;

  // Define target gaps for S/R levels
  const supportTargets = [currentPrice - 5, currentPrice - 10, currentPrice - 20];
  const resistanceTargets = [currentPrice + 5, currentPrice + 10, currentPrice + 20];
// console.log('Current Price:', currentPrice);
  // Find supports: for each target, pick the bin below currentPrice with highest volume and closest to target
  const supports = supportTargets.map(target => {
    return sorted
      .filter(l => l.price < currentPrice)
      .sort((a, b) => {
        // Closest to target, then highest volume
        const da = Math.abs(a.price - target);
        const db = Math.abs(b.price - target);
        if (da !== db) return da - db;
        return b.volume - a.volume;
      })[0]?.price ?? null;
  }).filter(v => v !== null);

  // Find resistances: for each target, pick the bin above currentPrice with highest volume and closest to target
  const resistances = resistanceTargets.map(target => {
    return sorted
      .filter(l => l.price > currentPrice)
      .sort((a, b) => {
        const da = Math.abs(a.price - target);
        const db = Math.abs(b.price - target);
        if (da !== db) return da - db;
        return b.volume - a.volume;
      })[0]?.price ?? null;
  }).filter(v => v !== null);

  return { poc, supports, resistances };
}

export function findLowHighSR(
  data: OHLCVData[],
  step = 1,
  topN = 3
): SRLevels {
  if (!data.length) return { poc: 0, supports: [], resistances: [] };

  const currentPrice = data[data.length - 1].c;

  // Count frequency of lows and highs at each price bin
  const lowBins: Record<string, number> = {};
  const highBins: Record<string, number> = {};

  data.forEach(bar => {
    const lowKey = (Math.round(bar.l / step) * step).toFixed(2);
    const highKey = (Math.round(bar.h / step) * step).toFixed(2);
    lowBins[lowKey] = (lowBins[lowKey] ?? 0) + 1;
    highBins[highKey] = (highBins[highKey] ?? 0) + 1;
  });

// Supports: most frequent lows below current price
const supports = Object.entries(lowBins)
  .map(([price, count]) => ({ price: parseFloat(price), count }))
  .filter(l => l.price < currentPrice)
  .sort((a, b) => b.count - a.count || b.price - a.price)
  .slice(0, topN)
  .map(l => l.price)
  .sort((a, b) => b - a); // <-- Order: S1 closest, S2 next, S3 furthest

// Resistances: most frequent highs above current price
const resistances = Object.entries(highBins)
  .map(([price, count]) => ({ price: parseFloat(price), count }))
  .filter(h => h.price > currentPrice)
  .sort((a, b) => b.count - a.count || a.price - b.price)
  .slice(0, topN)
  .map(h => h.price)
  .sort((a, b) => a - b); // <-- Order: R1 closest,

  // POC (optional): most frequent price overall (low or high)
  const allBins = { ...lowBins, ...highBins };
  const poc = Object.entries(allBins)
    .map(([price, count]) => ({ price: parseFloat(price), count }))
    .sort((a, b) => b.count - a.count)[0]?.price ?? 0;

  return { poc, supports, resistances };
}
